// What the page has met, kept in memory for a person's in-app report
// (ADR-0518). Nothing here leaves the browser by itself: the report panel
// shows every item and the person sends what they keep (decision 2a).
//
// **What is kept is a closed list, and this file is where it is closed.**
// - A `/v1` call: its method, its path TEMPLATE (`schemaPath`, never the URL,
//   which carries ids and query values), its status, how long it took and the
//   `X-Request-Id` the server answered (decision 3a). Never a header, a body, a
//   query value.
// - An uncaught error or unhandled rejection: its message and stack.
// - The last Problem a call was answered: its reason, message, request id and
//   each field's path, never a value.
// The last RING of each, newest last: N is ADR-0518 2a's, a bound of this
// build, and `ReportRequest`'s `maxItems` in api/openapi.yaml.
//
// **The mark** is the latest of the five events of decision 4a: an error the
// shell's boundary caught, a window `error`, an `unhandledrejection`, a Problem
// whose reason is `internal`, and a `5xx`. It sets a quiet dot on the report
// control and nothing else: no toast, no dialog, no focus moved (1a). A `4xx`
// other than those never marks, because a refusal is the product answering,
// and neither does a call that never reached the server (4b, not taken).
import type { Middleware } from "openapi-fetch";

export const RING = 20;

export type CallRecord = { method: string; path: string; status: number; duration_ms: number; request_id?: string; at: string };
export type ErrorRecord = { source: "boundary" | "error" | "unhandledrejection"; message: string; stack?: string; at: string };
export type ProblemRecord = { reason?: string; message?: string; request_id?: string; fields?: string[] };
/** What set the mark, in words a person reads in the panel. */
export type Noted = { what: string; at: string };

export interface Captured {
  calls: CallRecord[];
  errors: ErrorRecord[];
  problem?: ProblemRecord;
  noted?: Noted;
}

let state: Captured = { calls: [], errors: [] };
const listeners = new Set<() => void>();

function set(next: Captured) {
  state = next;
  for (const l of listeners) l();
}

function push<T>(ring: T[], item: T): T[] {
  return [...ring, item].slice(-RING);
}

function cut(s: string | undefined, max: number): string | undefined {
  return s === undefined ? undefined : s.length > max ? s.slice(0, max - 1) + "…" : s;
}

export function snapshot(): Captured {
  return state;
}

// Whether this deployment relays a report (`Me.in_app_report`, ADR-0527 4a),
// set by the shell's report control while it is drawn. A page's error
// boundary reads it to offer the report beside its failure (4a), and an
// `open` asks the control to show its panel.
let available = false;
export const OPEN_REPORT = "zenith:report-open";

export function setAvailable(on: boolean) {
  if (available === on) return;
  available = on;
  for (const l of listeners) l();
}

export function isAvailable(): boolean {
  return available;
}

export function openReport() {
  window.dispatchEvent(new Event(OPEN_REPORT));
}

export function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** Sets the mark to this event, replacing any earlier one (4a: latest only). */
function note(what: string) {
  set({ ...state, noted: { what, at: new Date().toISOString() } });
}

/** The panel's *Dismiss*: the mark is cleared and what was recorded stays. */
export function dismiss() {
  if (state.noted) set({ ...state, noted: undefined });
}

/** After a report is sent: the mark is cleared, and so is what it described,
 * so the next report does not carry this one's errors again. */
export function sent() {
  set({ calls: state.calls, errors: [], problem: undefined, noted: undefined });
}

/** An error the shell's boundary, or the window, met. */
export function noteError(source: ErrorRecord["source"], thrown: unknown) {
  const message = thrown instanceof Error ? thrown.message : typeof thrown === "string" ? thrown : "A value that is not an error was thrown.";
  const stack = thrown instanceof Error ? thrown.stack : undefined;
  set({ ...state, errors: push(state.errors, { source, message: cut(message, 2000) ?? "", stack: cut(stack, 8000), at: new Date().toISOString() }) });
  note(source === "boundary" ? "The page could not be drawn." : "The page met an error.");
}

/** The window's own two events. Returns what removes them. */
export function listen(target: Window = window): () => void {
  const onError = (e: ErrorEvent) => noteError("error", e.error ?? e.message);
  const onRejection = (e: PromiseRejectionEvent) => noteError("unhandledrejection", e.reason);
  target.addEventListener("error", onError);
  target.addEventListener("unhandledrejection", onRejection);
  return () => {
    target.removeEventListener("error", onError);
    target.removeEventListener("unhandledrejection", onRejection);
  };
}

const started = new Map<string, number>();

/** The client middleware that records each `/v1` call (3a). It reads the
 * response and never changes it: a Problem body is read from a clone. */
export const recordCalls: Middleware = {
  onRequest({ id }) {
    started.set(id, performance.now());
  },
  async onResponse({ id, request, response, schemaPath }) {
    const t0 = started.get(id);
    started.delete(id);
    const call: CallRecord = {
      method: request.method, path: schemaPath, status: response.status,
      duration_ms: t0 === undefined ? 0 : Math.round(performance.now() - t0),
      request_id: response.headers.get("X-Request-Id") ?? undefined, at: new Date().toISOString(),
    };
    let problem: ProblemRecord | undefined;
    if (!response.ok && (response.headers.get("Content-Type") ?? "").includes("json")) {
      try {
        const body = await response.clone().json() as { reason?: unknown; message?: unknown; request_id?: unknown; details?: { field?: unknown }[] };
        problem = {
          reason: typeof body.reason === "string" ? cut(body.reason, 80) : undefined,
          message: typeof body.message === "string" ? cut(body.message, 2000) : undefined,
          request_id: typeof body.request_id === "string" ? cut(body.request_id, 128) : undefined,
          fields: Array.isArray(body.details) ? body.details.map((d) => d?.field).filter((f): f is string => typeof f === "string").map((f) => cut(f, 200) ?? "").slice(0, 50) : undefined,
        };
      } catch {
        // Not a Problem after all: the call is still recorded, with no problem.
      }
    }
    set({ ...state, calls: push(state.calls, call), problem: problem ?? state.problem });
    if (response.status >= 500) note(`The server answered ${response.status}.`);
    else if (problem?.reason === "internal") note("The server reported an internal error.");
  },
  onError({ id, request, schemaPath }) {
    started.delete(id);
    // No answer: no status and no request id, which the server never logged.
    set({ ...state, calls: push(state.calls, { method: request.method, path: schemaPath, status: 0, duration_ms: 0, at: new Date().toISOString() }) });
  },
};

/** For tests only: forget everything. */
export function resetCapture() {
  available = false;
  set({ calls: [], errors: [] });
  started.clear();
}
